import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import { Subscription } from 'rxjs';

import { AnalyticsFacade } from '@amfam/shared/analytics/lib/+state/analytics.facade';
import { PageAnalyticParams } from '@amfam/shared/analytics/lib/models/analytics.model';
import { ContentDirective } from '@amfam/shared/utility/shared-services/lib/copy/content.directive';

import { SessionActivityService } from '../../login/session-activity/session-activity.service';

@Component({
  selector: 'ds-session-timeout',
  templateUrl: './session-timeout.component.html',
  styleUrls: ['./session-timeout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ContentDirective]
})
export class SessionTimeoutComponent implements OnInit, OnDestroy, OnChanges {
  private activityService = inject(SessionActivityService);
  private analyticsFacade = inject(AnalyticsFacade);
  private elem = inject(ElementRef);

  private showHideSub!: Subscription;
  private originalFocusElem: Element | null = null;

  readonly show = signal(false);
  // **** Start of Analytics data for this component
  private pageAnalytic: PageAnalyticParams = {
    pageName: 'MyAccount:SessionTimeOutWarning',
    experience: '',
    primaryCategory: 'My Account',
    subCategory1: 'MyAccount:SessionTimeoutEnroll',
    subCategory2: '',
    subCategory3: ''
  };

  ngOnInit() {
    // AS : Subscribe to the SessionActivityService function showSessionWarning$
    // which would tell us if its time to say bye - bye.
    this.showHideSub = this.activityService.showSessionWarning$.subscribe((status: boolean) => {
      this.show.set(status);
    });

    this.originalFocusElem = document.activeElement;
    this.elem.nativeElement.setAttribute('tabindex', '-1');
    this.elem.nativeElement.focus();
  }

  ngOnChanges() {
    if (this.show()) {
      this.analyticsFacade.trackPage(this.pageAnalytic);
    }
  }

  ngOnDestroy() {
    if (this.showHideSub) {
      this.showHideSub.unsubscribe();
    }
  }

  // AS : If the user wants to stay logged in call the service to extent his
  // session and reset all the timers and variables.
  continueSession() {
    this.activityService.keepAlive();
    this.show.set(false);
    this.restoreFocus();
  }

  // AS : If the user is done and wants to exit we call the service to
  // clean up after his mess and redirect to the login page.
  exitSession() {
    this.activityService.userDone();
  }

  private restoreFocus() {
    /*
      Reset the focus to the element that fired the modal

      The instanceof is a trick to add .focus to the element.
      this.originalFocusElemEl is from document.activeElement and is of type Element, not HTMLElement
      Element doesn't have .focus(), but HTMLElement does. Source: https://github.com/Microsoft/TypeScript/issues/5901#issuecomment-431649653
    */
    if (this.originalFocusElem instanceof HTMLElement) {
      this.originalFocusElem.focus(); // reset focus
      this.originalFocusElem = null; // clear variable
    }

    this.elem.nativeElement.removeAttribute('tabindex');
  }
}
